import type { Location, UserLocation } from '@/types';

export interface GeolocationError {
  code: number;
  message: string;
}

export class GeolocationService {
  private static watchId: number | null = null;

  /**
   * Get user's current location
   */
  static async getCurrentLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            coordinates: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            timestamp: position.timestamp,
          });
        },
        (error) => {
          reject({
            code: error.code,
            message: this.getErrorMessage(error.code),
          } as GeolocationError);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }

  /**
   * Watch user's location for changes
   */
  static watchLocation(
    onLocationUpdate: (location: UserLocation) => void,
    onError: (error: GeolocationError) => void
  ): number {
    if (!navigator.geolocation) {
      onError({
        code: 0,
        message: 'Geolocation is not supported by this browser',
      });
      return 0;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        onLocationUpdate({
          coordinates: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
          timestamp: position.timestamp,
        });
      },
      (error) => {
        onError({
          code: error.code,
          message: this.getErrorMessage(error.code),
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000, // 5 minutes
      }
    );

    this.watchId = watchId;
    return watchId;
  }

  /**
   * Stop watching location
   */
  static stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  /**
   * Calculate distance between two points using Haversine formula
   * Returns distance in miles
   */
  static calculateDistance(point1: Location, point2: Location): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(point2.latitude - point1.latitude);
    const dLon = this.toRadians(point2.longitude - point1.longitude);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(point1.latitude)) * Math.cos(this.toRadians(point2.latitude)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Get human-readable error message
   */
  private static getErrorMessage(code: number): string {
    switch (code) {
      case 1:
        return 'Location access denied by user';
      case 2:
        return 'Location unavailable';
      case 3:
        return 'Location request timeout';
      default:
        return 'Unknown location error';
    }
  }

  /**
   * Check if geolocation is available
   */
  static isAvailable(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Get approximate location from postcode or place name (UK)
   * Uses Nominatim (OpenStreetMap) API for geocoding
   */
  static async getLocationFromPostcode(query: string): Promise<Location | null> {
    try {
      // Clean and validate the query
      const cleanQuery = query.trim();
      if (!cleanQuery) return null;

      // Use Nominatim API for UK-focused geocoding
      const encodedQuery = encodeURIComponent(`${cleanQuery}, United Kingdom`);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=1&countrycodes=gb&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error(`Geocoding request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        return {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
        };
      }

      return null;
    } catch (error) {
      console.error('Postcode geocoding error:', error);
      return null;
    }
  }

  /**
   * Validate UK postcode format
   */
  static isValidUKPostcode(postcode: string): boolean {
    // UK postcode regex pattern
    const ukPostcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
    return ukPostcodeRegex.test(postcode.trim());
  }

  /**
   * Get default UK center coordinates (approximate center of UK)
   */
  static getUKCenter(): Location {
    return {
      latitude: 52.3555, // Birmingham area - geographic center of UK
      longitude: -1.1743,
    };
  }

  /**
   * Format coordinates for display
   */
  static formatCoordinates(location: Location, precision = 4): string {
    return `${location.latitude.toFixed(precision)}, ${location.longitude.toFixed(precision)}`;
  }
}